import { cn } from '@/lib/utils';
import Image, { ImageProps } from 'next/image';
import React from 'react';

type DynamicImageProps = ImageProps & {
  className?: string;
  src: string;
  alt: string;
  width?: number;
  height?: number;
  style?: React.CSSProperties;
  sizes?: string;
  onLoadingComplete?: () => void;
  disableAutoFade?: boolean;
};

const DynamicImage = ({
  src,
  alt,
  width,
  height,
  className,
  onLoadingComplete,
  style,
  sizes,
  disableAutoFade = false,
  ...props
}: DynamicImageProps) => {
  const blurDataURL = `data:image/svg+xml;base64,${toBase64(shimmer(width || 700, height || 475))}`;
  const [error, setError] = React.useState<React.SyntheticEvent<HTMLImageElement, Event> | null>(
    null
  );
  const [isLoading, setIsLoading] = React.useState(true);
  const [imageLoaded, setImageLoaded] = React.useState(false);
  const imageRef = React.useRef<HTMLImageElement>(null);

  const imageSrc = src;

  React.useEffect(() => {
    setError(null);
    setIsLoading(true);
    setImageLoaded(false);
  }, [src]);

  React.useEffect(() => {
    if (isLoading && !error) {
      const timer = setTimeout(() => {
        if (imageRef.current && imageRef.current.complete) {
          setIsLoading(false);
          setImageLoaded(true);
        }
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [isLoading, error]);

  const hasAmbientAnimation = className?.includes('animate-zoomSlideDown');

  const handleLoad = React.useCallback(
    (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
      setIsLoading(false);
      setImageLoaded(true);

      if (imageRef.current && !hasAmbientAnimation && !disableAutoFade) {
        imageRef.current.style.opacity = '1';
        void imageRef.current.offsetHeight;
      }

      onLoadingComplete?.();
    },
    [onLoadingComplete, hasAmbientAnimation]
  );

  const handleLoadingComplete = React.useCallback(
    (img: HTMLImageElement) => {
      setIsLoading(false);
      setImageLoaded(true);

      if (!hasAmbientAnimation && !disableAutoFade) {
        img.style.opacity = '1';
        void img.offsetHeight;
      }

      onLoadingComplete?.();
    },
    [onLoadingComplete, hasAmbientAnimation]
  );

  const handleError = React.useCallback((event: React.SyntheticEvent<HTMLImageElement, Event>) => {
    setError(event);
    setIsLoading(false);
    setImageLoaded(true);
  }, []);

  return (
    <Image
      ref={imageRef}
      className={cn(
        className,
        !disableAutoFade &&
          !hasAmbientAnimation && [
            isLoading && !imageLoaded ? 'bg-gray-200' : '',
            !imageLoaded ? 'opacity-0' : 'opacity-100',
            'transition-opacity duration-300 ease-in-out',
          ],
        hasAmbientAnimation && [!imageLoaded && 'invisible', imageLoaded && 'visible']
      )}
      src={error ? blurDataURL : imageSrc}
      alt={alt}
      width={width}
      height={height}
      sizes={sizes}
      placeholder='blur'
      style={style}
      blurDataURL={`/_next/image?url=${encodeURIComponent(src || '')}&w=16&q=1`}
      onError={handleError}
      onLoad={handleLoad}
      onLoadingComplete={handleLoadingComplete}
      {...props}
    />
  );
};

const shimmer = (w: number, h: number) => `
<svg width="${w}" height="${h}" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
  <defs>
    <linearGradient id="g">
      <stop stop-color="#f3f4f6" offset="20%" />
      <stop stop-color="#e5e7eb" offset="50%" />
      <stop stop-color="#f3f4f6" offset="70%" />
    </linearGradient>
  </defs>
  <rect width="${w}" height="${h}" fill="#f3f4f6" />
  <rect id="r" width="${w}" height="${h}" fill="url(#g)" />
  <animate xlink:href="#r" attributeName="x" from="-${w}" to="${w}" dur="1s" repeatCount="indefinite" />
</svg>
`;

const toBase64 = (str: string) =>
  typeof window === 'undefined' ? Buffer.from(str).toString('base64') : window.btoa(str);

export default DynamicImage;
